"use client";

import React, { useState } from "react";
import Image from "next/image";
import { ArrowUpRightIcon } from "@heroicons/react/16/solid";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";

import { getStrapiMedia } from "@/utils/api-helpers";

type AwardsAccordionSliderProps = {
  awards?: any[];
};

export default function AwardsAccordionSlider({
  awards = [],
}: AwardsAccordionSliderProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  if (!awards.length) {
    return (
      <div className="rounded-2xl border border-white/70 bg-white/45 px-6 py-10 text-[#5a6474] md:rounded-[1.5vw] md:px-[1.5vw] md:py-[2.5vw]">
        No awards available.
      </div>
    );
  }

  return (
    <div className="text-left mb-7">
      {awards.map((award, index) => {
        const isOpen = index === activeIndex;
        const awardsLogoSrc = award.awardsLogo?.url
          ? getStrapiMedia(award.awardsLogo.url)
          : "";
        const awardsImageSrc = award.awardsImage?.url
          ? getStrapiMedia(award.awardsImage.url)
          : "";

        return (
          <div key={index} className="border-b border-[#d3d2dd]">
            {/* Accordion Header */}
            <button
              type="button"
              className="w-full py-4 md:py-[1.2vw]"
              onClick={() =>
                setActiveIndex((prev) =>
                  prev === index ? null : index,
                )
              }
            >
              <div className="grid grid-cols-[40px_1fr_auto_auto] items-center gap-x-3 md:grid-cols-[6.25vw_1fr_auto_auto] md:gap-x-[2vw]">
                <div className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg  md:h-[3.85vw] md:w-[6.25vw] md:rounded-[0.5vw]">
                  {awardsLogoSrc ? (
                    <Image
                      src={awardsLogoSrc}
                      alt={
                        award.awardsLogo?.alternativeText ||
                        award.awardsTitle ||
                        "Award"
                      }
                      width={180}
                      height={120}
                      className="h-full w-full object-contain p-1 md:p-[0.4vw] grayscale "
                      loading="lazy"
                    />
                  ) : null}
                </div>

                <h3 className="text-left text-sm mt-auto mb-auto  font-semibold leading-[1.2] text-[#1f2c43] lg:text-[1.45vw] md:leading-[1.15]">
                  {award.awardsTitle}
                </h3>

                {award.year && (
                  <span className="leading-none text-[#1f2c43] lg:text-[1.45vw]">
                    {award.year}
                  </span>
                )}

                <span
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-all duration-300 md:h-[2.2vw] md:w-[2.2vw] ${isOpen
                    ? "border-[#851bed] bg-[#851bed] text-white rotate-90"
                    : "border-[#1f2c43] bg-transparent text-[#1f2c43]"
                    }`}
                >
                  <ArrowUpRightIcon className="h-4 w-4 md:h-[1.4vw] md:w-[1.4vw]" />
                </span>
              </div>
            </button>

            {/* Accordion Content */}
            <div
              className={`grid transition-all duration-300 ease-in-out ${isOpen
                ? "grid-rows-[1fr] opacity-100"
                : "grid-rows-[0fr] opacity-0"
                }`}
            >
              <div className="overflow-hidden pb-4 md:pb-[1.5vw]">
                <ScrollableCards
                  awardsImageSrc={awardsImageSrc}
                  awardsList={award.awardsList}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScrollableCards({
  awardsImageSrc,
  awardsList,
}: {
  awardsImageSrc: string;
  awardsList: any[];
}) {
  const [firstVisibleCard, setFirstVisibleCard] = useState(1);
  const [isScrollable, setIsScrollable] = useState(false);
  const [progress, setProgress] = useState(0);
  const totalCards = awardsList.length;
  const visibleCards = Math.min(awardsList.length, 2);
  const hasPhoto = !!awardsImageSrc;

  const checkScrollable = (swiper: SwiperType) => {
    setIsScrollable(!swiper.isLocked);
  };

  return (
    <div className="relative">
      {/* Pagination */}
      {isScrollable && (
        <div className="mb-2 text-right text-xs text-[#5a6474] md:mb-[0.8vw] md:text-[0.9vw]">
          <span className="font-semibold text-[#851bed]">
            {String(firstVisibleCard).padStart(2, "0")}
          </span>
          {" | "}
          {String(totalCards).padStart(2, "0")}
        </div>
      )}

      <div
        className="awardsAccordionSlider"
        data-cards={visibleCards}
        data-has-photo={hasPhoto ? "true" : "false"}
      >
        <Swiper
          slidesPerView={"auto"}
          spaceBetween={16}
          observer={true}
          observeParents={true}
          onSwiper={checkScrollable}
          onResize={checkScrollable}
          onProgress={(_, p) => setProgress(p)}
          onSlideChange={(swiper: SwiperType) => {
            const photoOffset = hasPhoto ? 1 : 0;
            setFirstVisibleCard(
              Math.max(1, swiper.activeIndex - photoOffset + 1),
            );
            checkScrollable(swiper);
          }}
          breakpoints={{
            768: {
              spaceBetween: 0,
            },
          }}
        >
          {/* Award photo - first slide, hidden on mobile via CSS */}
          {hasPhoto && (
            <SwiperSlide className="award-photo-slide">
              <div className="h-full w-full overflow-hidden rounded-2xl md:rounded-[1vw]">
                <Image
                  src={awardsImageSrc}
                  className="h-full w-full object-cover object-center"
                  alt="Award Image"
                  width={300}
                  height={280}
                />
              </div>
            </SwiperSlide>
          )}

          {/* Award card slides */}
          {awardsList.map((a: any, i: number) => (
            <SwiperSlide key={i} className="award-card-slide !bg-transparent">
              <div className="h-full overflow-hidden rounded-2xl border border-white/70 bg-white/50 p-5 backdrop-blur-md md:rounded-[1vw] md:p-[1.3vw]">
                {/* Header: brand name + logo */}
                <div>
                  <div className="mb-3 flex items-center justify-between md:mb-[0.8vw]">
                    <div className="">
                      <span className="text-xs font-medium text-[#5a6474] md:text-[0.75vw]">
                        {a.brandName}
                      </span>
                      {/* Campaign name */}
                      <h4 className="mb-3 font-semibold text-[#1f2c43] md:mb-[1vw] lg:text-[1vw]">
                        {a.campaignName}
                      </h4>
                    </div>

                    {a.brandLogo?.url && (
                      <Image
                        src={getStrapiMedia(a.brandLogo.url)}
                        alt={a.brandName || "Brand"}
                        width={80}
                        height={40}
                        className="h-6 w-auto object-contain md:h-[2vw]"
                      />
                    )}
                  </div>


                  {/* Divider */}
                  <hr className="mb-3 border-[#e5e7eb] md:mb-[1vw]" />

                  {/* Awards list */}
                  <ul className="flex flex-col gap-2 md:gap-[0.5vw]">
                    {a.awardItems.map((item: any, j: number) => (
                      <li
                        key={j}
                        className="flex items-start justify-between gap-3 md:gap-[1vw]"
                      >
                        <span className="text-xs leading-snug text-[#5a6474] lg:text-[0.9vw]">
                          {item.name}
                        </span>
                        <span className="whitespace-nowrap text-xs font-semibold text-[#1f2c43] lg:text-[0.9vw]">
                          {item.position}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {isScrollable && (
        <div className="mt-3 h-[3px] w-full rounded-full bg-black/10 md:mt-[0.8vw]">
          <div
            className="h-full rounded-full bg-[#851bed] transition-[width] duration-300"
            style={{
              width: `${((visibleCards / totalCards) + (1 - visibleCards / totalCards) * progress) * 100}%`,
            }}
          />
        </div>
      )}
    </div>
  );
}
